import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { Link, useNavigate } from "@tanstack/react-router"
import { findLast } from "lodash-es"
import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useState } from "react"


import { Button } from "@/components/Button/Button.tsx"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/DropdownMenu/DropdownMenu.tsx"
import { DropdownMenuStyles } from "@/components/DropdownMenu/DropdownMenuStyles.ts"
import { ErrorBoundary } from "@/components/ErrorBoundary/ErrorBoundary.tsx"
import { Flex, Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { Scrollable } from "@/components/Scrollable/Scrollable.tsx"
import { ScrollableModelImpl } from "@/components/Scrollable/ScrollableModel.ts"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import * as Tabs from "@/components/Tabs/Tabs.ts"
import { TabLink } from "@/components/Tabs/Tabs.ts"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { useModel } from "@/hooks/useModel.ts"
import { CheckmarkMedium, ChevronDownLight, Heart20Green, Icon16, Icon20, OpenInNewTabRegular } from "@/icons/Icons.ts"
import { openLinkInNewWindow } from "@/lib/std.ts"
import { TaskViewId } from "@/routes/tasks/TaskViewId.ts"
import { TaskRuntimeConfigFileTab } from "@/routes/tasks/TaskViewScene/InitRuntimeConfig/TaskRuntimeConfigFileTab.tsx"
import { TaskChangesTab } from "@/routes/tasks/TaskViewScene/TaskChangesTab.tsx"
import { TaskDevTab } from "@/routes/tasks/TaskViewScene/TaskDevTab.tsx"
import { GitHubDropdown } from "@/routes/tasks/TaskViewScene/TaskGitHubMenu.tsx"
import { TaskGuidelinesTab } from "@/routes/tasks/TaskViewScene/TaskGuidelinesTab.tsx"
import { TaskMoreMenu } from "@/routes/tasks/TaskViewScene/TaskMoreMenu.tsx"
import { allScriptsStarted } from "@/routes/tasks/TaskViewScene/TaskPreviewModel.ts"
import { TaskPreviewTab } from "@/routes/tasks/TaskViewScene/TaskPreviewTab.tsx"
import { TaskSummaryTab } from "@/routes/tasks/TaskViewScene/TaskSummaryTab.tsx"
import { isTabId, TabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"
import { versionTitle } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import { Rotate0, Rotate180 } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { Characters } from "@/styles/textStyles.ts"

import type { AiAgentTaskHistoryItem } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskTabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import type { MouseEvent, PropsWithChildren } from "react"

export const TaskResultTabs = observer((props: {
  model: TaskViewModel
}) => {
  const navigate = useNavigate()
  const currentTab = props.model.currentTab.get()
  const handleChange = useCallback((tab: string) => {
    isTabId(tab) && void navigate({ to: ".", search: (prevSearch) => ({ ...prevSearch, tab }) })
  }, [navigate])

  const previewProcessesState = props.model.previewProcessesState.get()
  const configuringRuntimePreview = props.model.task.get()?.initRuntimeConfigState != null
  const gitHubAvailable = props.model.gitHubModel.gitHubStatus.get()?.isGitHubRepo === true
  const versions = props.model.historyItems.get()
  const airLink = props.model.airDeepLink.get()

  const changedFilesCount = props.model.changesModel.files.get().length
  const totalErrorsCount = props.model.totalErrorsCount.get()

  const showGuidelinesTab = configuringRuntimePreview && props.model.taskSettings.get()?.generateGuidelinesInOnboarding === true

  // TODO(Matter-1147): change it when script status is stable
  const previewTabAvailable = !configuringRuntimePreview || allScriptsStarted(previewProcessesState)

  const currentVersion = findLast(versions, item => item.isActive)
  const currentVersionNumber = currentVersion ? currentVersion?.number : versions.length

  const scrollableModel = useModel(() => new ScrollableModelImpl())

  const devMode = useDevMode()

  const [visitedTabs, setVisitedTabs] = useState<Set<TaskTabId>>(new Set([currentTab]))

  const [versionDropdownOpen, setVersionDropdownOpen] = useState(false)
  const [hoveredVersionNumber, setHoveredVersionNumber] = useState<number | null>(null)

  useEffect(() => {
    setVisitedTabs((prev) => {
      if (prev.has(currentTab)) return prev
      const updated = new Set(prev)
      updated.add(currentTab)
      return updated
    })
  }, [currentTab])

  const [checkoutInProgress, setCheckoutInProgress] = useState(-1)

  const handleVersionSelect = useCallback((version: AiAgentTaskHistoryItem) => {
    setCheckoutInProgress(version.number)
    void props.model.checkout(version.commit.id, () => {
      setCheckoutInProgress(-1)
      setVersionDropdownOpen(false)
    })
  }, [props.model])

  // May reintroduce later
  const showAllVersionsLink = false

  // Early return to avoid layout shift
  if (props.model.changesModel.isLoading.get()) return null

  return (
    <Vertical flex="1 1 auto" gap="8px">
      {!configuringRuntimePreview && (
        <TaskResultToolbar>
          <VersionSelector>
            {versions.length <= 1
              ? "Initial version"
              : (
                <>
                  {showAllVersionsLink && (
                    <>
                      <AllVersionsLink
                        to="."
                        search={{ view: TaskViewId.Versions }}
                      >
                        All versions
                      </AllVersionsLink>
                      <VersionSelectorSeparator>{Characters.Nbsp}/{Characters.Nbsp}</VersionSelectorSeparator>
                    </>
                  )}
                  <DropdownMenu open={versionDropdownOpen} onOpenChange={setVersionDropdownOpen}>
                    <DropdownMenuTrigger asChild>
                      <VersionLink href="#" onClick={(e) => e.preventDefault()} data-testid="task-version-menu-trigger">
                        {versionTitle(currentVersionNumber)}
                        <ChevronDownLight className={cx(Icon16, versionDropdownOpen ? Rotate180 : Rotate0)} />
                      </VersionLink>
                    </DropdownMenuTrigger>
                    {versionDropdownOpen && (
                      <VersionDropdownContent align="start" data-testid="task-version-menu">
                        <Scrollable childrenContainerClassName={VersionsScrollable}>
                          {versions.slice().reverse().map((version) => (
                            <VersionDropdownItem
                              key={version.number}
                              data-testid={`task-version-menu-Version-${version.number}`}
                              onClick={(e) => {
                                e.preventDefault()
                                if (version.number !== currentVersionNumber) {
                                  handleVersionSelect(version)
                                }
                              }}
                              disabled={version.number === currentVersionNumber}
                              onMouseEnter={() => setHoveredVersionNumber(version.number)}
                              onMouseLeave={() => setHoveredVersionNumber(null)}
                              isActive={version.isActive}
                              className={cx(version.number === currentVersionNumber ? DropdownMenuStyles.SelectedItem : null)}
                            >
                              <Horizontal alignItems="center" gap="4px">
                                {version.number == 0 ? versionTitle(0) : (
                                  <>
                                    Version <VersionNumber>{version.number}</VersionNumber>
                                  </>
                                )}
                                {version.isSuccessful && (
                                  <Tooltip content="Marked as successful">
                                    <Heart20Green className={Icon16} />
                                  </Tooltip>
                                )}
                                <Spring />
                                {version.number !== currentVersionNumber && version.number !== checkoutInProgress && hoveredVersionNumber === version.number && (
                                  <RestoreText>click to restore</RestoreText>
                                )}
                                {version.number === currentVersionNumber && version.number !== checkoutInProgress && (
                                  <Tooltip content="Current version"><CheckmarkMedium className={Icon20} /></Tooltip>
                                )}
                                {version.number === checkoutInProgress && (
                                  <Tooltip content="Checkout in progress"><Flex><Spinner size={20} /></Flex></Tooltip>
                                )}
                              </Horizontal>
                            </VersionDropdownItem>
                          ))}
                        </Scrollable>
                      </VersionDropdownContent>
                    )}
                  </DropdownMenu>
                </>
              )}
          </VersionSelector>
          <Spring />
          <Horizontal gap="12px" alignItems="center">
            <TaskMoreMenu model={props.model} />
            {airLink && devMode && (
              <AirButton
                kind="alternative"
                size="large"
                onClick={() => openLinkInNewWindow(airLink)}
              >
                <Horizontal alignItems="center" gap="8px">
                  <OpenInNewTabRegular className={Icon20} />
                  Open in Air
                </Horizontal>
              </AirButton>
            )}
            {gitHubAvailable && (
              <GitHubDropdown model={props.model} />
            )}
          </Horizontal>
        </TaskResultToolbar>
      )}
      <Root value={currentTab} onValueChange={handleChange} activationMode="manual">
        <Tabs.List aria-label="Manage your task">
          {configuringRuntimePreview && (
            <TaskResultTabLink tabId={TabId.RuntimeConfigFile}>
              Configuration
            </TaskResultTabLink>
          )}

          {showGuidelinesTab && (
            <TaskResultTabLink tabId={TabId.Guidelines}>
              Guidelines
            </TaskResultTabLink>
          )}

          {previewTabAvailable && (
            <TaskResultTabLink tabId={TabId.Preview}>
              Preview
            </TaskResultTabLink>
          )}

          {!previewTabAvailable && (
            <Tooltip content="Preview becomes available when all scripts run successfully">
              <span>
                <TaskResultTabLink tabId={TabId.Preview} disabled>
                  Preview
                </TaskResultTabLink>
              </span>
            </Tooltip>
          )}

          {!configuringRuntimePreview && (
            <TaskResultTabLink tabId={TabId.Summary}>
              Summary
            </TaskResultTabLink>
          )}

          {!configuringRuntimePreview && (
            <TaskResultTabLink tabId={TabId.Changes}>
              Changed files
              {changedFilesCount > 0 && <TaskResultTabCounter>{changedFilesCount.toString()}</TaskResultTabCounter>}
            </TaskResultTabLink>
          )}

          {devMode && (
            <TaskResultTabLink tabId={TabId.Dev}>
              Dev
              {totalErrorsCount > 0 && <TaskResultTabWarningCounter>{totalErrorsCount}</TaskResultTabWarningCounter>}
            </TaskResultTabLink>
          )}
        </Tabs.List>

        <Scrollable
          model={scrollableModel}
          className={ScrollableTabsContent}
          childrenContainerClassName={ScrollableTabsChildrenContainer}
        >
          <ConditionalContent
            show={String(currentTab === TabId.RuntimeConfigFile)}
            value={TabId.RuntimeConfigFile}
            forceMount
          >
            {visitedTabs.has(TabId.RuntimeConfigFile) && (
              <ErrorBoundary>
                <TaskRuntimeConfigFileTab {...props} />
              </ErrorBoundary>
            )}
          </ConditionalContent>

          <ConditionalContent
            show={String(currentTab === TabId.Guidelines)}
            value={TabId.Guidelines}
            forceMount
          >
            {visitedTabs.has(TabId.Guidelines) && (
              <ErrorBoundary>
                <TaskGuidelinesTab {...props} />
              </ErrorBoundary>
            )}
          </ConditionalContent>

          <ConditionalContent show={String(currentTab === TabId.Preview)} value={TabId.Preview} forceMount>
            {visitedTabs.has(TabId.Preview) && (
              <ErrorBoundary>
                <TaskPreviewTab {...props} />
              </ErrorBoundary>
            )}
          </ConditionalContent>

          <ConditionalContent show={String(currentTab === TabId.Summary)} value={TabId.Summary} forceMount>
            {visitedTabs.has(TabId.Summary) && (
              <ErrorBoundary>
                <TaskSummaryTab {...props} />
              </ErrorBoundary>
            )}
          </ConditionalContent>

          <ConditionalContent show={String(currentTab === TabId.Changes)} value={TabId.Changes} forceMount>
            {visitedTabs.has(TabId.Changes) && (
              <ErrorBoundary>
                <TaskChangesTab {...props} scrollableModel={scrollableModel} />
              </ErrorBoundary>
            )}
          </ConditionalContent>

          {devMode && (
            <ConditionalContent show={String(currentTab === TabId.Dev)} value={TabId.Dev} forceMount>
              {visitedTabs.has(TabId.Dev) && (
                <ErrorBoundary>
                  <TaskDevTab {...props} scrollableModel={scrollableModel} />
                </ErrorBoundary>
              )}
            </ConditionalContent>
          )}
        </Scrollable>
      </Root>
    </Vertical>
  )
})

const TaskResultToolbar = styled(Horizontal)`
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  margin-top: -4.5px;
`

const VersionSelector = styled.span`
  ${textStyles.smallFontSize};
`

const VersionSelectorSeparator = styled(SecondaryTextSpan)`
  padding: 0 4px;
`

const AllVersionsLink = styled(Link)`
  color: var(--secondary-text-color);

  &:hover {
    color: var(--text-color);
  }
`

const VersionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  color: var(--secondary-text-color);

  &:hover {
    color: var(--text-color);
    background-color: var(--background-color);
  }

  &:focus-visible {
    outline: none;
  }
`

const VersionDropdownContent = styled(DropdownMenuContent)`
  max-height: 334px;

  ${textStyles.smallFontSize};
`

const VersionDropdownItem = styled(DropdownMenuItem)<{ isActive: boolean }>`
  color: ${props => props.isActive ? "var(--text-color)" : "var(--secondary-text-color)"};

  width: 240px;
  white-space: nowrap;
`

const VersionNumber = styled.span`
  font-variant-numeric: tabular-nums;
`

const RestoreText = styled(SecondaryTextSpan)`
  ${textStyles.smallFontSize};
`

const TaskResultTabCounter = styled.span`
  margin-left: 4px;
  font-variant-numeric: tabular-nums;

  font-weight: 600;
`

const TaskResultTabWarningCounter = styled(TaskResultTabCounter)`
  color: var(--warning-color);
`

type TaskResultTabLinkProps = PropsWithChildren & {
  tabId: TaskTabId,
  disabled?: boolean,
}

const TaskResultTabLink = ({ disabled = false, ...props }: TaskResultTabLinkProps) => {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
  }

  return (
    <Tabs.Trigger value={props.tabId} disabled={disabled}>
      <TabLink to="." search={{ tab: props.tabId }} onClick={handleClick} tabIndex={-1} disabled={disabled}>
        {props.children}
      </TabLink>
    </Tabs.Trigger>
  )
}

const Root = styled(Tabs.Root)`
  display: flex;
  flex-direction: column;
  flex: 1;

  max-width: 100%;
  height: 0;
`

const VersionsScrollable = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ScrollableTabsContent = css`
  margin: 12px 0 0;
`

const ScrollableTabsChildrenContainer = css`
  height: 100%;
`

const ConditionalContent = styled(Tabs.Content)<{
  show: string
}>`
  display: ${props => props.show === "true" ? "block" : "none"};
`

const AirButton = styled(Button)`
  gap: 8px;

  padding: 8px 10px;

  font-weight: 400;
`